import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format, startOfDay, addDays } from "date-fns";
import { fr } from "date-fns/locale";
import { Video, Phone, Home as HomeIcon, Clock, CalendarCheck, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import Reveal from "../components/site/Reveal";
import { PageHero } from "../components/site/Common";
import { CITIES, CONTACT } from "../mock";
import { api, errMsg } from "../lib/api";
import ConsentCheck from "../components/site/ConsentCheck";
import SlotPicker from "../components/site/SlotPicker";
import PropertyFields, { EMPTY_PROPERTY, toPayload } from "../components/site/PropertyFields";

const TYPES = [
  { id: "visio", label: "Visio", desc: "30 min en visioconférence", icon: Video },
  { id: "tel", label: "Téléphone", desc: "20 min, on vous appelle", icon: Phone },
  { id: "visite", label: "Visite du logement", desc: "45 min sur place, gratuit", icon: HomeIcon },
];

export default function RendezVous() {
  const [type, setType] = useState("visio");
  const [date, setDate] = useState();
  const [slot, setSlot] = useState("");
  const [step, setStep] = useState(0);
  const [f, setF] = useState({ name: "", email: "", phone: "", city: "Saint-Malo", message: "" });
  const [done, setDone] = useState(null);
  const [prop, setProp] = useState(EMPTY_PROPERTY);
  const [showProp, setShowProp] = useState(false);
  const visit = type === "visite";
  const pickAddress = ({ city, postcode }) => {
    const match = CITIES.find((c) => c.name.toLowerCase() === (city || "").toLowerCase());
    if (match) set("city", match.name);
    else { set("city", city); toast.message(`${postcode} ${city} : nous vérifions que votre commune est bien dans notre secteur.`); }
  };
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const [avail, setAvail] = useState({ blocked: [], booked: {} });
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const today = startOfDay(new Date());

  const loadAvail = useCallback(() => {
    api.availability(format(today, "yyyy-MM-dd"), format(addDays(today, 61), "yyyy-MM-dd")).then(setAvail).catch(() => {});
  }, []); // eslint-disable-line
  useEffect(() => { loadAvail(); }, [loadAvail]);


  const submit = async () => {
    if (!f.name.trim()) return toast.error("Merci d'indiquer votre nom");
    if (!/\S+@\S+\.\S+/.test(f.email)) return toast.error("Adresse email invalide");
    if (f.phone.replace(/\D/g, "").length < 10) return toast.error("Numéro de téléphone invalide");
    if (visit && prop.address.trim().length < 5) return toast.error("Merci d'indiquer l'adresse du logement à visiter");
    if (prop.listing_url && !/^https?:\/\//.test(prop.listing_url.trim())) return toast.error("Le lien de l'annonce doit commencer par https://");
    if (!consent) return toast.error("Merci de cocher la case de consentement");
    const rdv = { ...f, type, date: format(date, "yyyy-MM-dd"), slot, consent: true, property: toPayload(prop) };
    setSending(true);
    try {
      const res = await api.appointment(rdv);
      setDone({ ...rdv, meet_url: res?.meet_url });
      toast.success("Rendez-vous confirmé !");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(errMsg(e));
      if (e?.response?.status === 409) { loadAvail(); setStep(0); setSlot(""); }
    } finally { setSending(false); loadAvail(); }
  };

  const T = TYPES.find((t) => t.id === type);

  return (
    <div data-testid="rdv-page">
      <PageHero kicker="Rendez-vous" title="Prenez rendez-vous en 1 minute" text={`Choisissez le format et le créneau qui vous conviennent. Nous sommes disponibles du lundi au samedi, de 10h à 18h.`} />
      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <Reveal className="bg-white rounded-[36px] shadow-[0_40px_90px_-40px_rgba(19,38,28,0.5)] ring-1 ring-[#13261c]/5 overflow-hidden">
            {done ? (
              <div className="p-8 sm:p-14 text-center animate-in fade-in zoom-in-95 duration-500" data-testid="rdv-confirmation">
                <span className="mx-auto w-16 h-16 rounded-full bg-[#13261c] grid place-items-center"><Check className="w-8 h-8 text-[#d9895a]" strokeWidth={3} /></span>
                <h2 className="font-display text-4xl font-semibold text-[#13261c] mt-6">C'est noté, {done.name.split(" ")[0]} !</h2>
                <p className="mt-3 text-lg text-[#5a6158]">{TYPES.find((t) => t.id === done.type).label} le <strong className="text-[#13261c]">{format(new Date(done.date + "T12:00:00"), "EEEE d MMMM", { locale: fr })}</strong> à <strong className="text-[#13261c]">{done.slot.replace(":", "h")}</strong></p>
                {done.meet_url && (
                  <div className="mt-6 mx-auto max-w-md rounded-3xl bg-[#f3ede1] p-5" data-testid="rdv-meet">
                    <p className="text-sm font-bold text-[#13261c] flex items-center justify-center gap-2"><Video className="w-4 h-4 text-[#c4703f]" />Votre lien visio</p>
                    <a href={done.meet_url} target="_blank" rel="noopener noreferrer" data-testid="rdv-meet-link" className="mt-2 block text-[#c4703f] font-semibold break-all hover:underline">{done.meet_url}</a>
                    <p className="mt-2 text-xs text-[#6d7268]">À ouvrir le jour J depuis votre navigateur, sans rien installer.</p>
                  </div>
                )}
                <p className="mt-4 text-[#6d7268]">Une confirmation avec l'invitation calendrier vous a été envoyée à {done.email}.</p>
                <div className="mt-8 flex flex-wrap justify-center gap-3">
                  <Link to="/" className="rounded-full bg-[#13261c] hover:bg-[#2a4636] text-white px-7 py-4 font-bold transition-colors">Retour à l'accueil</Link>
                  <button onClick={() => { setDone(null); setProp(EMPTY_PROPERTY); setShowProp(false); setStep(0); setDate(undefined); setSlot(""); setConsent(false); }} data-testid="rdv-new" className="rounded-full ring-1 ring-[#dccfb6] px-7 py-4 font-bold text-[#13261c] hover:bg-[#f1ebdd] transition-colors">Nouveau rendez-vous</button>
                </div>
              </div>
            ) : (
              <div className="grid lg:grid-cols-12">
                <aside className="lg:col-span-4 bg-[#13261c] text-[#faf7f0] p-8 lg:p-10 relative grain">
                  <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#d9895a]">Ouest Conciergerie</p>
                  <h3 className="font-display text-3xl font-semibold mt-3">{T.label}</h3>
                  <p className="mt-2 text-[#faf7f0]/80">{T.desc}</p>
                  <div className="mt-8 space-y-4 text-[#faf7f0]/90">
                    <p className="flex gap-3 items-center"><CalendarCheck className="w-5 h-5 text-[#d9895a]" />{date ? format(date, "EEEE d MMMM yyyy", { locale: fr }) : "Choisissez une date"}</p>
                    <p className="flex gap-3 items-center"><Clock className="w-5 h-5 text-[#d9895a]" />{slot ? slot.replace(":", "h") : "Choisissez un horaire"}</p>
                  </div>
                  <p className="mt-10 text-sm text-[#faf7f0]/70">Un imprévu ? Appelez-nous au {CONTACT.phone}.</p>
                </aside>
                <div className="lg:col-span-8 p-6 sm:p-10">
                  {step === 0 ? (
                    <div className="animate-in fade-in duration-300">
                      <p className="font-bold text-[#13261c] mb-3">1. Format du rendez-vous</p>
                      <div className="grid sm:grid-cols-3 gap-3">
                        {TYPES.map((t) => {
                          const on = t.id === type;
                          return (
                            <button key={t.id} onClick={() => setType(t.id)} data-testid={`rdv-type-${t.id}`}
                              className={`rounded-2xl p-4 text-left ring-1 transition-colors ${on ? "bg-[#13261c] text-white ring-[#13261c]" : "ring-[#e6dcc8] hover:bg-[#f6f1e7] text-[#13261c]"}`}>
                              <t.icon className={`w-6 h-6 ${on ? "text-[#d9895a]" : "text-[#c4703f]"}`} />
                              <p className="font-bold mt-2">{t.label}</p>
                              <p className={`text-xs mt-0.5 ${on ? "text-white/75" : "text-[#6d7268]"}`}>{t.desc}</p>
                            </button>
                          );
                        })}
                      </div>
                      <div className="mt-8 grid md:grid-cols-2 gap-6">
                        <div className="md:col-span-2"><SlotPicker date={date} slot={slot} onDate={setDate} onSlot={setSlot} avail={avail} /></div>
                      </div>
                      <div className="mt-10 flex justify-end">
                        <button disabled={!date || !slot} onClick={() => setStep(1)} data-testid="rdv-continue"
                          className="group inline-flex items-center gap-2 rounded-full bg-[#13261c] hover:bg-[#2a4636] disabled:opacity-40 disabled:cursor-not-allowed text-white px-8 py-4 font-bold transition-colors">
                          Continuer <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="animate-in fade-in slide-in-from-right-4 duration-300 space-y-3">
                      <p className="font-bold text-[#13261c] mb-1">4. Vos coordonnées</p>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <Input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Nom et prénom *" className="h-14 rounded-2xl text-base" data-testid="rdv-name" />
                        <Input value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="Email *" className="h-14 rounded-2xl text-base" data-testid="rdv-email" />
                        <Input value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Téléphone *" className="h-14 rounded-2xl text-base" data-testid="rdv-phone" />
                        <Select value={f.city} onValueChange={(v) => set("city", v)}>
                          <SelectTrigger className="h-14 rounded-2xl text-base" data-testid="rdv-city"><SelectValue /></SelectTrigger>
                          <SelectContent>{[...CITIES.map((c) => c.name), ...(f.city && !CITIES.some((c) => c.name === f.city) ? [f.city] : [])].map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="pt-4" data-testid="rdv-property">
                        {visit || showProp ? (
                          <div className="rounded-3xl bg-[#f6f1e7] p-4 sm:p-6 animate-in fade-in duration-300">
                            <p className="font-bold text-[#13261c] mb-1">{visit ? "5. Le logement à visiter" : "5. Votre logement"}</p>
                            <p className="text-sm text-[#6d7268] mb-4">{visit ? "L'adresse est obligatoire pour que l'on vienne chez vous. Le reste nous aide à préparer la visite." : "Facultatif : ça nous permet de préparer votre rendez-vous."}</p>
                            <PropertyFields value={prop} onChange={setProp} required={visit} onPick={pickAddress} />
                          </div>
                        ) : (
                          <button type="button" onClick={() => setShowProp(true)} data-testid="rdv-property-toggle" className="w-full rounded-2xl border-2 border-dashed border-[#d9cdb4] px-5 py-4 text-left font-bold text-[#13261c] hover:bg-[#f6f1e7] transition-colors">
                            + Ajouter les infos de mon logement <span className="font-normal text-[#6d7268]">(facultatif : adresse, chambres, annonce actuelle…)</span>
                          </button>
                        )}
                      </div>
                      <Textarea value={f.message} onChange={(e) => set("message", e.target.value)} placeholder="Un message, une question ? (facultatif)" className="min-h-[100px] rounded-2xl text-base" data-testid="rdv-message" />
                      <div className="pt-2"><ConsentCheck checked={consent} onChange={setConsent} testid="rdv-consent" /></div>
                      <div className="pt-6 flex justify-between gap-3">
                        <button onClick={() => setStep(0)} className="inline-flex items-center gap-2 rounded-full px-6 py-4 font-bold text-[#2c3a31] hover:bg-[#f1ebdd] transition-colors" data-testid="rdv-back"><ArrowLeft className="w-5 h-5" />Retour</button>
                        <button onClick={submit} disabled={sending} data-testid="rdv-submit" className="rounded-full bg-[#d9895a] hover:bg-[#e39d72] disabled:opacity-60 text-[#13261c] px-8 py-4 font-bold transition-colors">{sending ? "Envoi…" : "Confirmer le rendez-vous"}</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
